import React, { useState, useEffect } from "react";
import { LengthUnit, ForceUnit } from "../../../store/useUnitStore";

interface CreateProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateProject: (config: {
    projectName: string;
    projectLocation: string;
    lengthUnit: LengthUnit;
    forceUnit: ForceUnit;
    directoryHandle?: any;
  }) => void;
}

export const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  isOpen,
  onClose,
  onCreateProject,
}) => {
  const [projectName, setProjectName] = useState("");
  const [projectLocation, setProjectLocation] = useState("");
  const [directoryHandle, setDirectoryHandle] = useState<any>(null);
  const [lengthUnit, setLengthUnit] = useState<LengthUnit>("meter");
  const [forceUnit, setForceUnit] = useState<ForceUnit>("kiloNewton");
  const [error, setError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const isElectron = typeof window !== "undefined" && window.electronAPI;
  const supportsFileSystemAccess =
    "showDirectoryPicker" in window && !isElectron;

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setProjectName("");
      setProjectLocation("");
      setDirectoryHandle(null);
      setLengthUnit("meter");
      setForceUnit("kiloNewton");
      setError("");
      setIsCreating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBrowse = async () => {
    setError("");

    if (isElectron) {
      try {
        const result = await window.electronAPI.openFile({
          properties: ["openDirectory", "createDirectory"],
          title: "Select Project Location",
        });

        if (!result.canceled && result.filePaths && result.filePaths[0]) {
          const selectedPath = result.filePaths[0];

          // Prevent saving to system folders
          const systemFolders = [
            "C:\\Windows",
            "C:\\Program Files",
            "C:\\Program Files (x86)",
            "/System",
            "/Library",
            "/usr",
            "/bin",
            "/sbin",
          ];

          const isSystemFolder = systemFolders.some((sysPath) =>
            selectedPath.toLowerCase().startsWith(sysPath.toLowerCase())
          );

          if (isSystemFolder) {
            setError(
              "Cannot save to system folders. Please choose Documents, Desktop, or another user folder."
            );
            return;
          }

          setProjectLocation(selectedPath);
        }
      } catch (err: any) {
        setError("Failed to select folder. Please try a different location.");
      }
    } else if (supportsFileSystemAccess) {
      try {
        const dirHandle = await (window as any).showDirectoryPicker({
          mode: "readwrite",
        });
        setDirectoryHandle(dirHandle);
        setProjectLocation(dirHandle.name);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError("Failed to select folder");
        }
      }
    }
  };

  const handleCreate = () => {
    setError("");

    // Validation
    if (!projectName.trim()) {
      setError("Project name is required");
      return;
    }

    if (isElectron && !projectLocation) {
      setError("Please select a project location");
      return;
    }

    if (!isElectron && supportsFileSystemAccess && !directoryHandle) {
      setError("Please select a project location");
      return;
    }

    setIsCreating(true);

    onCreateProject({
      projectName: projectName.trim(),
      projectLocation: isElectron
        ? projectLocation
        : directoryHandle?.name || "",
      lengthUnit,
      forceUnit,
      directoryHandle: !isElectron ? directoryHandle : undefined,
    });
  };

  const handleCancel = () => {
    setProjectName("");
    setProjectLocation("");
    setDirectoryHandle(null);
    setError("");
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === "Enter" &&
      projectName.trim() &&
      (projectLocation || directoryHandle)
    ) {
      handleCreate();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div
          className="bg-gray-800 rounded-lg shadow-xl w-[700px] max-w-[90vw] border border-gray-700 max-h-[90vh] overflow-hidden flex flex-col"
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-2xl"></span>
              <h2 className="text-lg font-semibold text-white">
                Create New Project
              </h2>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isCreating}
                autoFocus
              />
            </div>

            {/* Project Location */}
            {(isElectron || supportsFileSystemAccess) && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Project Location <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={projectLocation || "Not selected"}
                    readOnly
                    className={`flex-1 px-3 py-2 bg-gray-700 border rounded text-sm ${
                      projectLocation
                        ? "border-green-500 text-white"
                        : "border-gray-600 text-gray-400"
                    }`}
                    disabled
                  />
                  <button
                    onClick={handleBrowse}
                    disabled={isCreating}
                    className="px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                      />
                    </svg>
                    Browse
                  </button>
                </div>
                {isElectron && (
                  <p className="text-xs text-gray-400 mt-1">
                    💡 Recommended: Documents, or a custom project folder
                  </p>
                )}
              </div>
            )}

            {/* Unit Configuration */}
            <div className="border-t border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-300 mb-4">
                Unit Configuration <span className="text-red-500">*</span>
              </h3>

              <div className="grid grid-cols-2 gap-6">
                {/* Length Units */}
                <div>
                  <h4 className="text-xs font-bold text-gray-400 mb-3">
                    Length Units
                  </h4>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto pr-2">
                    {[
                      "inch",
                      "foot",
                      "millimeter",
                      "centimeter",
                      "decimeter",
                      "meter",
                      "kilometer",
                    ].map((unit) => (
                      <label
                        key={unit}
                        className="flex items-center cursor-pointer hover:bg-gray-700 p-2 rounded transition-colors"
                      >
                        <input
                          type="radio"
                          name="lengthUnit"
                          value={unit}
                          checked={lengthUnit === unit}
                          onChange={(e) =>
                            setLengthUnit(e.target.value as LengthUnit)
                          }
                          className="w-4 h-4 text-blue-600"
                          disabled={isCreating}
                        />
                        <span className="ml-3 text-sm text-gray-300 capitalize">
                          {unit === "kilometer"
                            ? "Kilometer"
                            : unit === "millimeter"
                              ? "Millimeter"
                              : unit === "centimeter"
                                ? "Centimeter"
                                : unit === "decimeter"
                                  ? "Decimeter"
                                  : unit.charAt(0).toUpperCase() +
                                    unit.slice(1)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Force Units */}
                <div>
                  <h4 className="text-xs font-bold text-gray-400 mb-3">
                    Force Units
                  </h4>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto pr-2">
                    {[
                      "pound",
                      "kiloPound",
                      "kilogram",
                      "metricTon",
                      "newton",
                      "decaNewton",
                      "kiloNewton",
                      "megaNewton",
                    ].map((unit) => (
                      <label
                        key={unit}
                        className="flex items-center cursor-pointer hover:bg-gray-700 p-2 rounded transition-colors"
                      >
                        <input
                          type="radio"
                          name="forceUnit"
                          value={unit}
                          checked={forceUnit === unit}
                          onChange={(e) =>
                            setForceUnit(e.target.value as ForceUnit)
                          }
                          className="w-4 h-4 text-blue-600"
                          disabled={isCreating}
                        />
                        <span className="ml-3 text-sm text-gray-300">
                          {unit === "kiloPound"
                            ? "KiloPound"
                            : unit === "metricTon"
                              ? "Metric Ton"
                              : unit === "decaNewton"
                                ? "DecaNewton"
                                : unit === "kiloNewton"
                                  ? "KiloNewton"
                                  : unit === "megaNewton"
                                    ? "MegaNewton"
                                    : unit.charAt(0).toUpperCase() +
                                      unit.slice(1)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Configuration Preview */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3 text-sm text-blue-200">
              <div className="font-semibold mb-1">Selected Configuration:</div>
              <div className="text-xs space-y-0.5">
                <div>
                  Length:{" "}
                  <span className="text-blue-300 font-medium capitalize">
                    {lengthUnit}
                  </span>
                </div>
                <div>
                  Force:{" "}
                  <span className="text-blue-300 font-medium">
                    {forceUnit === "kiloPound"
                      ? "KiloPound"
                      : forceUnit === "metricTon"
                        ? "Metric Ton"
                        : forceUnit === "decaNewton"
                          ? "DecaNewton"
                          : forceUnit === "kiloNewton"
                            ? "KiloNewton"
                            : forceUnit === "megaNewton"
                              ? "MegaNewton"
                              : forceUnit.charAt(0).toUpperCase() +
                                forceUnit.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-500 rounded text-red-300 text-sm">
                ⚠️ {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-gray-700 flex-shrink-0">
            <button
              onClick={handleCancel}
              disabled={isCreating}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={
                isCreating ||
                !projectName.trim() ||
                ((isElectron || supportsFileSystemAccess) &&
                  !projectLocation &&
                  !directoryHandle)
              }
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50 min-w-[100px] flex items-center justify-center gap-2"
            >
              {isCreating ? (
                "Creating..."
              ) : (
                <>
                  <span>✨</span>
                  <span>Create Project</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
